import axios from "axios";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

function authHeaders() {
  return {
    "Content-Type": "application/json",
    token: sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken"),
  };
}

function request(path, method = "get", { data, params, listKey } = {}) {
  return axios({
    method,
    url: `${API_BASE_URL}/${path}`,
    headers: authHeaders(),
    data,
    params,
  })
    .then((res) => {
      // res.data.result can legitimately be `null` (e.g. "no saved targeting yet") - that's
      // different from the one endpoint (presigned-url upload) that has no `result` key at
      // all, which is what the res.data fallback below is for. `??` can't tell those apart
      // since both are nullish, so check key presence instead of the value.
      const result = res.data && "result" in res.data ? res.data.result : res.data;
      // List endpoints wrap their array as { total, <resourceName>: [...], page, limit }
      // rather than returning a bare array - unwrap it here so callers always get an array.
      return listKey ? result?.[listKey] ?? [] : result;
    })
    .catch((error) => {
      console.error(`Error ${method.toUpperCase()} ${path}:`, error.response?.data || error.message);
      throw error;
    });
}

// Advertiser types
export const getAdvertiserTypes = () => request("advertisement/advertiser-types", "get", { listKey: "advertiserTypes" });

// Ad accounts
export const createAdAccount = (data) => request("advertisement/ad-accounts", "post", { data });
export const getAdAccounts = (params) =>
  request("advertisement/ad-accounts", "get", { params, listKey: "adAccounts" });
export const getAdAccount = (adAccountId) => request(`advertisement/ad-accounts/${adAccountId}`);
export const updateAdAccount = (adAccountId, data) =>
  request(`advertisement/ad-accounts/${adAccountId}`, "patch", { data });
export const deleteAdAccount = (adAccountId) => request(`advertisement/ad-accounts/${adAccountId}`, "delete");

// Campaigns
export const createCampaign = (adAccountId, data) =>
  request(`advertisement/ad-accounts/${adAccountId}/campaigns`, "post", { data });
export const getCampaigns = (adAccountId, params) =>
  request(`advertisement/ad-accounts/${adAccountId}/campaigns`, "get", { params, listKey: "campaigns" });
export const getCampaign = (campaignId) => request(`advertisement/campaigns/${campaignId}`);
export const updateCampaign = (campaignId, data) =>
  request(`advertisement/campaigns/${campaignId}`, "patch", { data });
export const deleteCampaign = (campaignId) => request(`advertisement/campaigns/${campaignId}`, "delete");
export const pauseCampaign = (campaignId) => request(`advertisement/campaigns/${campaignId}/pause`, "patch");
export const resumeCampaign = (campaignId) => request(`advertisement/campaigns/${campaignId}/resume`, "patch");

// Fetches every page of a list endpoint (page/limit, defaults page 1/limit 10 per the docs) -
// keeps requesting the next page until one comes back shorter than the requested limit, so
// callers get the true full list regardless of how many records actually exist, instead of
// silently getting only the first page's worth.
async function fetchAllPages(fetchPage, limit = 50) {
  let page = 1;
  let all = [];
  while (true) {
    const items = await fetchPage({ page, limit });
    all = all.concat(items);
    if (items.length < limit) break;
    page += 1;
  }
  return all;
}
export const getAllCampaigns = (adAccountId) => fetchAllPages((params) => getCampaigns(adAccountId, params));

// Ad sets
export const createAdSet = (campaignId, data) =>
  request(`advertisement/campaigns/${campaignId}/ad-sets`, "post", { data });
export const getAdSets = (campaignId, params) =>
  request(`advertisement/campaigns/${campaignId}/ad-sets`, "get", { params, listKey: "adSets" });
export const getAllAdSets = (campaignId) => fetchAllPages((params) => getAdSets(campaignId, params));
export const getAdSet = (adSetId) => request(`advertisement/ad-sets/${adSetId}`);
export const updateAdSet = (adSetId, data) => request(`advertisement/ad-sets/${adSetId}`, "patch", { data });
export const deleteAdSet = (adSetId) => request(`advertisement/ad-sets/${adSetId}`, "delete");
export const pauseAdSet = (adSetId) => request(`advertisement/ad-sets/${adSetId}/pause`, "patch");
export const resumeAdSet = (adSetId) => request(`advertisement/ad-sets/${adSetId}/resume`, "patch");
export const createAdSetTargeting = (adSetId, data) =>
  request(`advertisement/ad-sets/${adSetId}/targeting`, "post", { data });
export const getAdSetTargeting = (adSetId) => request(`advertisement/ad-sets/${adSetId}/targeting`);
export const updateAdSetTargeting = (adSetId, data) =>
  request(`advertisement/ad-sets/${adSetId}/targeting`, "put", { data });

// Interests (public endpoint, no auth required) - each row is a whole interest category
// (id, category, tags), not an individual tag. AdSetTargeting.interest_ids references these row ids.
export const getInterestList = () => request("interest/list", "post", { data: {}, listKey: "interests" });

// Ads (creatives)
export const getAdCtas = (objective) =>
  request(`advertisement/ads/ctas`, "get", { params: { objective } });
export const createAd = (adSetId, data) => request(`advertisement/ad-sets/${adSetId}/ads`, "post", { data });
export const getAds = (adSetId, params) =>
  request(`advertisement/ad-sets/${adSetId}/ads`, "get", { params, listKey: "ads" });
export const getAllAds = (adSetId) => fetchAllPages((params) => getAds(adSetId, params));
export const getAd = (adId) => request(`advertisement/ads/${adId}`);
export const updateAd = (adId, data) => request(`advertisement/ads/${adId}`, "patch", { data });
export const deleteAd = (adId) => request(`advertisement/ads/${adId}`, "delete");
export const submitAd = (adId) => request(`advertisement/ads/${adId}/submit`, "patch");
export const pauseAd = (adId) => request(`advertisement/ads/${adId}/pause`, "patch");
export const resumeAd = (adId) => request(`advertisement/ads/${adId}/resume`, "patch");

// Ad reporting (buyer-facing, ads.routes.js)
export const getAdReportReasons = () =>
  request("advertisement/ads/report-reasons", "get", { listKey: "reasons" });
export const reportAd = (adId, data) => request(`advertisement/ads/${adId}/reports`, "post", { data });

// Ad hiding preferences (buyer-facing, ad-preferences.routes.js, mounted at /ads) - responses
// aren't wrapped in { result }, so request()'s `res.data?.result ?? res.data` fallback returns
// the body itself ({ success, message, ... }) unchanged.
export const hideAd = (data) => request("ads/preferences/hide", "post", { data });
export const hideSimilarAds = (data) => request("ads/preferences/hide-similar", "post", { data });

// Ad billing - these routes are mounted at top-level ad-account/... and billing/..., not
// nested under advertisement/... like the rest of this file (routes.js mounts each
// modules/advertisement/routes/*.routes.js file at /<basename>, and the billing route files are
// named ad-account.routes.js / billing.routes.js).
export const getBillingProfile = () => request("ad-account/billing");
export const upsertBillingProfile = (data) => request("ad-account/billing", "post", { data });
export const getCurrencyConfig = () => request("ad-account/currency");
export const updateCurrency = (data) => request("ad-account/currency", "patch", { data });
export const getPostpaidEligibility = () => request("billing/postpaid/eligibility");
export const addCredits = (data) => request("billing/credits", "post", { data });
export const retryPayment = () => request("billing/payment/retry", "post");
export const getTaxProfile = () => request("ad-account/tax-profile");
export const upsertTaxProfile = (data) => request("ad-account/tax-profile", "post", { data });
export const getSpendingControls = () => request("ad-account/spending-controls");
export const updateSpendingControls = (data) => request("ad-account/spending-controls", "patch", { data });
export const getSpendSummary = () => request("ad-account/spend-summary");
export const listInvoices = (params) => request("billing/invoices", "get", { params });
export const listTransactions = (params) => request("billing/transactions", "get", { params });
export const getInvoiceDetail = (invoiceId) => request(`billing/invoices/${invoiceId}`);

// Postpaid stakeholder decision: no card is saved/auto-charged - this is the only place a
// postpaid charge actually happens, always with a payment_method_id collected right now.
export const payInvoice = (invoiceId, data) => request(`billing/invoices/${invoiceId}/pay`, "post", { data });

// The PDF is generated fresh on every call (no stored file) - this is the same authenticated
// route the Flutter app calls too, so it needs the auth token header, not a plain <a href>.
export const getInvoicePdfBlob = (invoiceId) =>
  axios({
    method: "get",
    url: `${API_BASE_URL}/billing/invoices/${invoiceId}/download`,
    headers: authHeaders(),
    responseType: "blob",
  }).then((res) => res.data);
export const listPaymentMethods = () => request("billing/payment-methods");
export const addPaymentMethod = (data) => request("billing/payment-methods", "post", { data });
export const removePaymentMethod = (paymentMethodId) =>
  request(`billing/payment-methods/${paymentMethodId}`, "delete");
export const setDefaultPaymentMethod = (paymentMethodId) =>
  request(`billing/payment-methods/${paymentMethodId}/default`, "patch");

// Buyer-facing ad delivery (api/BUYER_AD_DELIVERY.md) - mounted at ads/delivery/... by
// ad-preferences.routes.js. Use via src/hook/useSponsoredAds.js rather than directly, so the
// auction -> reserve-impression-before-render order is kept in one place.
export const runAdAuction = (data) => request("ads/delivery/auction", "post", { data });
export const reserveAdImpression = (data) => request("ads/delivery/impression", "post", { data });
export const trackAdEvent = (data) => request("ads/delivery/events", "post", { data });

/**
 * Same presigned-S3-upload flow used by the seller-onboarding form
 * (src/components/sellerInformation/SellerInformation.js's uploadFiles).
 */
export async function uploadAdMedia(files) {
  if (!files || files.length === 0) return [];

  const fileData = files.map((file) => ({ fileName: file.name, fileType: file.type }));
  const presignResponse = await axios.post(
    `${API_BASE_URL}/aws/generate-presigned-url`,
    { files: fileData },
    { headers: authHeaders() }
  );

  if (!presignResponse.data.success) {
    throw new Error("Failed to generate pre-signed URLs");
  }

  const preSignedUrls = presignResponse.data.directory;

  await Promise.all(
    preSignedUrls.map((item, index) =>
      axios.put(item.presignedUrl, files[index], {
        headers: { "Content-Type": files[index].type, "Content-Encoding": "binary" },
      })
    )
  );

  return preSignedUrls.map((item) => ({
    url: item.fileUrl,
    media_type: item.fileType.startsWith("image") ? "image" : "video",
  }));
}

/**
 * `/buyer/get-products` is a generic, platform-wide product search with no auto-scoping to
 * the caller - it takes whatever `filters.seller_id` is passed, it doesn't derive it from the
 * auth token. So getting "my products" is two calls: fetch the caller's own Seller row first
 * (via /seller/get-seller-profile with no seller_id, which self-scopes), then filter by its id.
 */
export const getMySellerId = () =>
  request("seller/get-seller-profile", "post", { data: {} }).then((profile) => profile?.id ?? null);

export const getMyProducts = (sellerId) =>
  request("buyer/get-products", "post", {
    data: { filters: { seller_id: sellerId }, page: 1, limit: 100 },
    listKey: "products",
  });

/**
 * No endpoint returns a flat list of "all my campaigns" (with their ads) across every ad
 * account, so the dashboard fans out: ad-accounts -> campaigns per account -> ad-sets per
 * campaign -> ads per ad-set, then attaches each campaign's own ads to it (flattened across
 * its ad sets). Every campaign is included even if it has no ad sets/ads yet (`ads: []`), so
 * a freshly-created campaign never silently disappears from the dashboard.
 */
export async function getAllMyCampaigns() {
  const adAccounts = await getAdAccounts();

  const campaignsPerAccount = await Promise.all(
    adAccounts.map(async (adAccount) => {
      const campaigns = await getAllCampaigns(adAccount.id);

      return Promise.all(
        campaigns.map(async (campaign) => {
          const adSets = await getAllAdSets(campaign.id);
          const adsPerAdSet = await Promise.all(adSets.map((adSet) => getAllAds(adSet.id)));
          return { ...campaign, ads: adsPerAdSet.flat(), ad_account: adAccount };
        })
      );
    })
  );

  return campaignsPerAccount.flat();
}

// Ad performance insights - params: { from, to (YYYY-MM-DD, inclusive UTC days), breakdown }
export const getAccountInsights = (params) => request("advertisement/insights", "get", { params });
export const getCampaignInsights = (campaignId, params) =>
  request(`advertisement/campaigns/${campaignId}/insights`, "get", { params });
export const getAdSetInsights = (adSetId, params) =>
  request(`advertisement/ad-sets/${adSetId}/insights`, "get", { params });
export const getAdInsights = (adId, params) => request(`advertisement/ads/${adId}/insights`, "get", { params });
